<template>
    <form @submit="onSubmit" :action="`/account/booking/save/${booking.reference}`" method="post" class="max-w-7xl grid grid-cols-7 gap-6 mx-auto">
        <input type="hidden" name="_token" :value="csrf">
        <input type="hidden" name="id" :value="booking.id">

        <div class="col-span-7 md:col-span-5 space-y-6">
           <div class="mt-4 px-4 md:px-0">
                <div class="bg-brand-blue py-4 text-2xl text-white px-3 md:px-6">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 inline-block mb-1" viewBox="0 0 20 20"
                        fill="currentColor">
                        <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                            clip-rule="evenodd" />
                    </svg>

                    Personal Information
                </div>
                <div class="bg-white p-8 border-b-2 border-gray-300 border-opacity-70">
                    <div class="grid grid-cols-4 gap-6">
                        <div class="col-span-4 md:col-span-2">
                            <text-input
                                label="First name"
                                autocomplete="given-name"
                                name="first_name"
                                v-model="first_name"
                            ></text-input>

                        </div>

                        <div class="col-span-4 md:col-span-2">
                            <text-input
                                label="Last name"
                                autocomplete="family-name"
                                :validation="v$.last_name"
                                name="last_name"
                                v-model="last_name"
                            ></text-input>
                        </div>

                        <div class="md:col-start-1 col-span-4 md:col-span-2">
                            <text-input
                                label="Email address"
                                autocomplete="email"
                                :validation="v$.email"
                                name="email"
                                v-model="email"
                            ></text-input>

                        </div>

                        <div class="col-span-4 md:col-span-2">
                            <text-input
                                label="Phone number"
                                autocomplete="telephone"
                                name="phone"
                                v-model="phone"
                            ></text-input>

                        </div>

                        <div class="col-span-4 md:col-span-2">
                            <text-input
                                label="Party size"
                                type="number"
                                :validation="v$.party_size"
                                name="party_size"
                                v-model="v$.party_size.$model"
                            ></text-input>

                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-12">
                <div class="bg-brand-blue py-4 text-2xl text-white px-6">
                    <i class="fas fa-info-circle"></i>

                    Booking Dates
                </div>
                <div class="bg-white p-4 border-b-2 border-gray-300 border-opacity-70">
                    <p class="text-gray-700">
                        To change your arrival or return dates please contact the office.
                    </p>
                </div>
            </div>

            <div class="mt-12">
                <div class="bg-brand-blue py-4 text-2xl text-white px-6">
                    <i class="fas fa-plane"></i>

                    Flight Information
                </div>
                <div class="bg-white p-8 border-b-2 border-gray-300 border-opacity-70">
                    <div class="grid grid-cols-4 gap-6">
                        <div class="col-span-4 md:col-span-2">
                            <text-input
                                label="Returning flight number"
                                v-model="returning_flight_number"
                                name="returning_flight_number"
                                styling="uppercase"
                            ></text-input>
                        </div>

                        <div class="col-span-4 md:col-span-2">
                            <text-input
                                label="Destination"
                                v-model="destination"
                                name="destination"
                                styling="capitalize"
                            ></text-input>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-12">
                <div class="bg-brand-blue py-4 text-2xl text-white px-6">
                    <i class="fas fa-car"></i>

                    Vehicle Information
                </div>
                <div class="bg-white p-4 md:p-8">
                    <div class="grid grid-cols-4 gap-6">
                        <div class="col-span-4 md:col-span-2">
                            <TextInput
                                label="Vehicle Registration"
                                autocomplete="off"
                                styling="uppercase"
                                name="vehicle_registration"
                                v-model="vehicle_registration"
                            ></TextInput>
                        </div>
                        <div class="col-span-4 md:col-span-2">

                            <DataListInput
                                label="Vehicle Make"
                                v-model="vehicle_make"
                                name="vehicle_make"
                                :options="vehicleMakeList"
                                option-slug="vehicle-make-list"
                            ></DataListInput>
                        </div>

                        <div class="col-span-4 md:col-span-2">
                            <text-input
                                label="Vehicle Colour"
                                autocomplete="off"
                                name="vehicle_colour"
                                v-model="vehicle_colour"
                            ></text-input>
                        </div>

                    </div>
                </div>
            </div>
        </div>
        <div class="col-span-7 md:col-span-2 space-y-6">
            <div class="mt-4">
                <SummaryCard
                    :booking="booking"
                    :entry="booking.entry"
                    :exit="booking.exit"
                    :vehicle-type-id="booking.vehicle_type_id"
                    :vehicle-registration="vehicle_registration"
                    :party-size="party_size"
                    :valet="valet"
                    :show-save-btn="true"
                ></SummaryCard>
            </div>
        </div>
    </form>

    <!-- <popup-modal
        :show="showPopupModal"
        :success="false"
        title="Error"
        :message="errorMessage"

        @close="showPopupModal = false"
    ></popup-modal> -->
</template>

<script>
import useVuelidate from '@vuelidate/core';
import {
    required,
    email,
    helpers,
    minValue,
} from '@vuelidate/validators';
import TextInput from '../TextInput.vue';
import DataListInput from '../DataListInput.vue';
import EditBookingSummaryCard from './Booking/EditBookingSummaryCard.vue';
import PopupModal from './PopupModal.vue';

const { withMessage } = helpers;

export default {
    setup() {
        return {
            v$: useVuelidate(),
        };
    },
    data() {
        return {
            first_name: this.booking.customer.first_name,
            last_name: this.booking.customer.last_name,
            email: this.booking.customer.email,
            phone: this.booking.customer.phone,
            party_size: this.booking.party_size,
            returning_flight_number: this.booking.returning_flight_number,
            destination: this.booking.destination,
            vehicle_registration: this.booking.vehicle_registration,
            vehicle_make: this.booking.vehicle_make,
            vehicle_colour: this.booking.vehicle_colour,
            csrf: document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
            saveBtnText: 'Update Booking',
            valet: this.booking.valet,
        };
    },
    validations() {
        return {
            first_name: {
                required: withMessage('First name is required', required),
            },
            last_name: {
                required: withMessage('Last name is required', required),
            },
            email: {
                required: withMessage('Email address is required', required),
                email: withMessage('Not a valid email', email),
            },
            party_size: {
                required: withMessage('Party size is required', required),
                minValue: withMessage('You need at least one passenger', minValue(1)),
            },
        };
    },
    components: {
        TextInput,
        SummaryCard: EditBookingSummaryCard,
        DataListInput,
    },
    props: {
        booking: { type: Object, required: true },
    },
    methods: {
        async onSubmit(e) {
            const result = await this.v$.$validate();

            if (!result) {
                e.preventDefault();
                return false;
            }

            return true;
        },
    },
};
</script>

<style scoped>

select[disabled] { @apply bg-gray-200 }

</style>
